<script setup>
import {useRoute, useRouter} from "vue-router";
import {onMounted, ref} from "vue";
import createApiService from "@/services/api.js";
import {useAuthenticationStore} from "@/stores/authentication.js";
import {useConfigStore} from "@/stores/config.js";
import {useSnackbarStore} from "@/stores/snackbar.js";
import {getMaterialChecks} from "@/helpers/constants.js";
import {useI18n} from "vue-i18n";
import {translateErrors} from "@/helpers/translateErrors.js";
import MaterialImageCaptureUpload from "@/components/material/MaterialImageCaptureUpload.vue";
import {useUnsavedChangesPrompt} from "@/composables/useUnsavedChangesPrompt.js";

definePage({
  meta: {
    layout: 'minimal',
  },
});

const router = useRouter();
const route = useRoute();
const api = createApiService(router);
const project = ref({});
const {t} = useI18n();
const checks = ref(Object.keys(getMaterialChecks(t)));
const differences = ref(false);
const release = ref(true);
const loading = ref(false);
const imageFiles = ref([]);
const imageProductFiles = ref([]);
const differencesDescription = ref("");
const releaseDescription = ref("");
const date = new Date().toISOString().slice(0, 10);
const authStore = useAuthenticationStore();
const configStore = useConfigStore();
const snackbarStore = useSnackbarStore();
const hasUnsavedChanges = ref(false);
const submitting = ref(false);
const errors = ref({});

const markUnsavedChanges = () => {
  hasUnsavedChanges.value = true;
};

useUnsavedChangesPrompt(hasUnsavedChanges, t("material.page.unsavedChanges"));

onMounted(() => {
  updateProject();
});

const updateProject = async () => {
  loading.value = true;
  try {
    const response = await api.get("materialCheckProjects", {params: {id: route.params.id}});
    project.value = response.data.data[0];
  } catch (error) {
    console.log(error?.response?.data?.data ?? error);
  } finally {
    loading.value = false;
  }
};

const submitMaterialCheck = async () => {
  submitting.value = true;
  errors.value = {};

  try {
    const formData = new FormData();
    formData.append("project_id", String(Number(route.params.id)));
    formData.append("employee_id", String(configStore.currentEmployeeId ?? ""));
    checks.value.forEach((check) => formData.append("checks[]", check));
    if (differences.value) {
      formData.append("differences_description", differencesDescription.value);
    }
    if (!release.value) {
      formData.append("release_description", releaseDescription.value);
    }

    imageFiles.value.forEach((file) => {
      if (file instanceof File) {
        formData.append("delivery_note_images[]", file);
      }
    });
    imageProductFiles.value.forEach((file) => {
      if (file instanceof File) {
        formData.append("product_images[]", file);
      }
    });

    await api.post("materialCheckCreate", formData);
    hasUnsavedChanges.value = false;
    snackbarStore.showMessage(t("material.page.successSaved"), "success");
    await router.push("/material");
  } catch (error) {
    errors.value = error.response?.data?.data || {};
    snackbarStore.showMessage("Materiaalcheck opslaan mislukt", "error");
  } finally {
    submitting.value = false;
  }
};

</script>

<template>
  <v-card>
    <v-card-title>{{ t("material.page.title") }}</v-card-title>
    <v-card-text>
      <h4 class="mb-2">{{ t("material.page.projectDetails") }}</h4>
      <v-table class="mb-5">
        <tr>
          <th>{{ t("material.page.project") }}</th>
          <td>{{ project.name }}</td>
        </tr>
        <tr>
          <th>{{ t("material.page.projectNumber") }}</th>
          <td>{{ project.project_number }}</td>
        </tr>
        <tr>
          <th>{{ t("material.page.date") }}</th>
          <td>{{ date }}</td>
        </tr>
        <tr>
          <th>{{ t("material.page.siteEmployee") }}</th>
          <td>{{ authStore.getCurrentUserFullName() }}</td>
        </tr>
      </v-table>

      <template v-for="(purchase_order, index) in project.purchase_orders" :key="purchase_order.id">
        <h4 class="mb-2">{{ t("material.page.delivery") }} {{ project.purchase_orders.length > 1 ? index + 1 : '' }}</h4>
        <v-table class="mb-5">
          <tr>
            <th>{{ t("material.page.supplier") }}</th>
            <td>{{ purchase_order.supplier }}</td>
          </tr>
          <tr>
            <th>{{ t("material.page.orderNumber") }}</th>
            <td>{{ purchase_order.order_number }}</td>
          </tr>
        </v-table>
      </template>

      <v-btn-toggle
        v-model="checks"
        color="indufastGreen"
        base-color="indufastRed"
        multiple
        class="check-button-group"
        direction="vertical"
        @update:model-value="markUnsavedChanges"
      >
        <template v-for="(label, key) in getMaterialChecks(t)" :key="key">
          <v-btn :value="key" class="check-button mb-2 pa-2">
            <v-icon
              class="mr-2"
              :icon="checks.indexOf(key) !== -1 ? 'mdi-check' : 'mdi-close-outline'"
            />
            {{ label }}
          </v-btn>
        </template>
      </v-btn-toggle>

      <h4 class="mb-2">{{ t("material.page.differences") }}</h4>
      <v-btn-toggle
        v-model="differences"
        :color="differences ? 'indufastRed' : 'indufastGreen'"
        mandatory
        class="check-button-group"
        direction="vertical"
        @update:model-value="markUnsavedChanges"
      >
        <v-btn :value="false" class="check-button mb-2 pa-2">
          <v-icon
            class="mr-2"
            icon="mdi-check"
          />
          {{ t("common.no") }}
        </v-btn>
        <v-btn :value="true" class="check-button mb-2 pa-2">
          <v-icon
            class="mr-2"
            icon="mdi-close-outline"
          />
          {{ t("common.yes") }}
        </v-btn>
      </v-btn-toggle>
      <v-textarea
        v-if="differences"
        v-model="differencesDescription"
        :label="t('material.page.differencesDescription')"
        :error-messages="translateErrors(errors.differences_description, t('material.page.differencesDescription'), true, t)"
        autofocus
        @update:model-value="markUnsavedChanges"
      >
      </v-textarea>

      <h4 class="mb-2">{{ t("material.page.release") }}</h4>
      <v-btn-toggle
        v-model="release"
        :color="release ? 'indufastGreen' : 'indufastRed'"
        mandatory
        class="check-button-group"
        direction="vertical"
        @update:model-value="markUnsavedChanges"
      >
        <v-btn :value="true" class="check-button mb-2 pa-2">
          <v-icon
            class="mr-2"
            icon="mdi-check"
          />
          {{ t("common.yes") }}
        </v-btn>
        <v-btn :value="false" class="check-button mb-2 pa-2">
          <v-icon
            class="mr-2"
            icon="mdi-close-outline"
          />
          {{ t("common.no") }}
        </v-btn>
      </v-btn-toggle>
      <v-textarea
        v-if="!release"
        v-model="releaseDescription"
        :label="t('material.page.remarks')"
        :error-messages="translateErrors(errors.release_description, t('material.page.remarks'), true, t)"
        autofocus
        @update:model-value="markUnsavedChanges"
      >
      </v-textarea>

      <h4 class="mb-2">{{ t("material.imageCapture.deliveryNoteTitle") }}</h4>
      <material-image-capture-upload v-model="imageFiles" @update:model-value="markUnsavedChanges" />
      <v-textarea
        v-if="errors.delivery_note_images?.length"
        :error-messages="translateErrors(errors.delivery_note_images, t('material.imageCapture.deliveryNoteTitle'), true, t)"
        class="error-messages-only"
      />

      <h4 class="mb-2">{{ t("material.imageCapture.productPhotosTitle") }}</h4>
      <material-image-capture-upload v-model="imageProductFiles" @update:model-value="markUnsavedChanges" />
      <v-textarea
        v-if="errors.product_images?.length"
        :error-messages="translateErrors(errors.product_images, t('material.imageCapture.productPhotosTitle'), true, t)"
        class="error-messages-only"
      />

      <v-btn
        class="mt-4"
        color="indufastGreen"
        block
        :loading="submitting"
        :disabled="loading || submitting"
        @click="submitMaterialCheck"
      >
        {{ t("common.save") }}
      </v-btn>
    </v-card-text>
  </v-card>
</template>

<style scoped>
th {
  text-align: left;
  font-weight: normal;
  white-space: nowrap;
  width: 0;
  padding-right: 10px;
}

.check-button-group {
  width: 100%;
}

.check-button {
  width: 100%;
  justify-content: flex-start;
}


</style>
